import React from 'react';
import { FiX, FiArrowLeft, FiClock, FiCheck } from 'react-icons/fi';
import { Sheet } from '../../../../../components/Sheet/Sheet';
import DatePickerWrapper from '../../../../../components/DatePickerWrapper/DatePickerWrapper';
import * as translator from '../../../../../utils/translator';
import { formatTimestamp, formatDateStamp } from '../../../../../utils/timestampFormatter';
import { TIMEZONES } from '../../../../../utils/consts';
import { ConsultationTimeSlot } from 'data-types';
import { useConsultationBooking } from '../hooks/useConsultationBooking';
import { localeByLanguage } from '../careProfileAssets';
import { MainTracker } from '../../../../../PixelTrackers/MainTracker';

const bt = (language: string, key: string, fallback: string): string => {
	const value = translator.translate(language, 'my_account', key);
	if (typeof value !== 'string' || !value || value === key || value.includes(key)) {
		return fallback;
	}
	return value;
};


interface BookConsultationSheetProps {
	isOpen: boolean;
	onClose: () => void;
	country: string;
	language: string;
	onBooked?: () => void;
	/** Preferred doctor when the customer has a dedicated assignment. */
	preferredDoctorID?: number | null;
	/** Role the user opened booking for — currently only doctor is Timekit-backed. */
	providerRole?: 'doctor' | 'nutritionist';
}


type Step = 'date' | 'time' | 'confirm' | 'done';

const toYMDInTimeZone = (date: Date, timeZone: string): string =>
	date.toLocaleDateString('en-CA', { timeZone });

const ConfirmDetailRows: React.FC<{ rows: Array<{ label: string; value: string }> }> = ({ rows }) => (
	<div className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-3 items-baseline mb-8">
		{rows.map((row) => (
			<React.Fragment key={row.label}>
				<span className="text-sm text-[#616975] whitespace-nowrap">{row.label}</span>
				<span className="text-[15px] font-semibold text-care-plus-heading break-words">{row.value}</span>
			</React.Fragment>
		))}
	</div>
);

const BookConsultationSheet: React.FC<BookConsultationSheetProps> = ({
	isOpen,
	onClose,
	country,
	language,
	onBooked,
	preferredDoctorID = null,
	providerRole = 'doctor',
}) => {
	const { doctors, loading, booking, error, loadAvailability, book } = useConsultationBooking({ country, providerType: providerRole });
	const [step, setStep] = React.useState<Step>('date');
	const [selectedDate, setSelectedDate] = React.useState<string>('');
	const [selectedSlot, setSelectedSlot] = React.useState<ConsultationTimeSlot | null>(null);
	const wasOpenRef = React.useRef(false);
	const locale = localeByLanguage[language] || 'en-US';
	const timeZone = TIMEZONES[country.toUpperCase() as keyof typeof TIMEZONES] ?? 'Europe/Berlin';

	// Reset only on open edge so soft refetch / remount mid-done does not bounce back to date.
	React.useEffect(() => {
		if (isOpen && !wasOpenRef.current) {
			setStep('date');
			setSelectedDate('');
			setSelectedSlot(null);
			loadAvailability();
		}
		wasOpenRef.current = isOpen;
	}, [isOpen]);

	// preferredDoctorID is a DOCTOR care-team id and must only steer doctor bookings.
	// For nutritionist bookings the availability list holds caretakers (each `doctorID` is a
	// caretakerID), so matching the doctor's id here would pick the wrong provider — or fall
	// through to doctors[0] — which is how a nutritionist booking ended up booking a doctor.
	const preferredProviderID = providerRole === 'doctor' ? preferredDoctorID : null;
	const doctor = preferredProviderID != null
		? doctors.find((d) => d.doctorID === preferredProviderID) ?? doctors[0] ?? null
		: doctors[0] ?? null;

	// Slots are already sorted ascending by the API — earliest is the first timeslot of the first day.
	const earliestSlot = doctor?.availability?.[0]?.timeslots?.[0] ?? null;
	const availableDates = new Set((doctor?.availability ?? []).map((a) => a.date));
	const slotsForDate = doctor?.availability.find((a) => a.date === selectedDate)?.timeslots ?? [];

	const formatTime = (iso: string) =>
		formatTimestamp(iso, {
			showDate: false,
			showTime: true,
			use24Hour: true,
			useNumericTimeFormat: true,
			locale,
			timeZone,
		});
	const formatLongDate = (iso: string) =>
		formatTimestamp(iso, {
			showDate: true,
			showTime: false,
			showDay: true,
			showYear: true,
			useFullDay: true,
			useFullMonth: true,
			locale,
			timeZone,
		});
	const formatShortDate = (ymd: string) =>
		formatDateStamp(`${ymd}T12:00:00`, {
			showDay: false,
			showYear: false,
			useFullMonth: true,
			locale,
			timeZone,
		});

	const providerLabelKey =
		providerRole === 'nutritionist' ? 'CareProfile/CareTeam/Nutritionist' : 'CareProfile/CareTeam/Doctor';

	const handleDatePick = (value: string) => {
		if (!value) return;
		setSelectedDate(value);
		setSelectedSlot(null);
		setStep('time');
	};

	const handleSlotPick = (slot: ConsultationTimeSlot) => {
		setSelectedSlot(slot);
		setStep('confirm');
	};

	const handleConfirm = () => {
		if (!doctor || !selectedSlot) return;
		const payload = providerRole === 'nutritionist'
			? { providerType: 'nutritionist' as const, providerID: doctor.doctorID, start: selectedSlot.start, end: selectedSlot.end }
			: { doctorID: doctor.doctorID, start: selectedSlot.start, end: selectedSlot.end };
		book(payload, () => {
			MainTracker.track('custom', 'Consultation Booked', { provider_type: providerRole, booked_date: selectedSlot.start });
			setStep('done');
			if (onBooked) onBooked();
		});
	};

	const title =
		step === 'date'
			? translator.translate(language, 'my_account', 'CareProfile/Booking/SelectDate')
			: step === 'time'
				? translator.translate(language, 'my_account', 'CareProfile/Booking/AvailableTimes')
				: step === 'confirm'
					? translator.translate(language, 'my_account', 'CareProfile/Booking/ConfirmTitle')
					: translator.translate(language, 'my_account', 'CareProfile/Booking/ConfirmedTitle');

	const goBack = () => {
		if (step === 'time') setStep('date');
		else if (step === 'confirm') setStep('time');
	};

	return (
		<Sheet isOpen={isOpen} onClose={onClose}>
			<div id="care-profile-booking" className="flex flex-col w-full">
				{/* Header */}
				<div className="relative flex items-center justify-center px-3 py-4 flex-shrink-0">
					{(step === 'time' || step === 'confirm') && (
						<button
							id="care-profile-booking-back"
							type="button"
							onClick={goBack}
							aria-label="Back"
							className="absolute left-2 flex items-center justify-center w-8 h-8 rounded-full border-none bg-transparent text-care-plus-heading cursor-pointer"
						>
							<FiArrowLeft size={20} />
						</button>
					)}
					<p className="text-[20px] font-semibold text-care-plus-heading text-center leading-[1.2] px-8">{title}</p>
					<button
						id="care-profile-booking-close"
						type="button"
						onClick={onClose}
						aria-label="Close"
						className="absolute right-2 flex items-center justify-center w-8 h-8 rounded-full border-none bg-transparent text-care-plus-heading cursor-pointer"
					>
						<FiX size={19} />
					</button>
				</div>

				<div className="px-5 pb-6">
					{loading && (
						<p className="text-center text-[13px] text-[#99a3b2] py-8">
							{translator.translate(language, 'my_account', 'CareProfile/Booking/Loading')}
						</p>
					)}

					{!loading && !doctor && (
						<p className="text-center text-[13px] text-[#99a3b2] py-8">
							{translator.translate(language, 'my_account', 'CareProfile/Booking/NoAvailability')}
						</p>
					)}

					{/* Step: date */}
					{!loading && doctor && step === 'date' && (
						<div id="care-profile-booking-date">
							<p className="text-center text-sm text-[#616975] mb-4">
								{translator.translate(language, 'my_account', 'CareProfile/Booking/SelectDateSubtitle')}
							</p>
							{earliestSlot && (
								<p
									id="care-profile-booking-earliest-slot"
									className="text-center text-[12px] text-care-plus-orange font-medium mb-3"
								>
									{bt(language, 'CareProfile/Booking/EarliestSlot', 'Earliest available')}
									{': '}
									{formatLongDate(earliestSlot.start)} · {formatTime(earliestSlot.start)}
								</p>
							)}
							<DatePickerWrapper
								id="care-profile-booking-calendar"
								inline
								variant="appointment"
								isBirthdayInput={false}
								value={selectedDate}
								errorMode={false}
								minDate={new Date()}
								filterDate={(date: Date) => availableDates.has(toYMDInTimeZone(date, timeZone))}
								onChange={handleDatePick}
							/>
						</div>
					)}

					{/* Step: time */}
					{!loading && doctor && step === 'time' && (
						<div id="care-profile-booking-time">
							<p className="text-center text-sm text-[#616975] mb-4">
								{translator
									.translate(language, 'my_account', 'CareProfile/Booking/AvailableSlotsFor')
									.replace('{{date}}', formatShortDate(selectedDate))}
							</p>
							<div className="grid grid-cols-2 gap-3">
								{slotsForDate.map((slot) => {
									const isSelected = selectedSlot?.start === slot.start;
									return (
										<button
											key={slot.start}
											type="button"
											onClick={() => handleSlotPick(slot)}
											className={`flex items-center gap-x-2 rounded-[10px] px-3 py-3 border border-solid text-[14px] font-medium transition-all duration-200 ${
												isSelected
													? 'bg-care-plus-button-gradient text-white border-transparent'
													: 'bg-white text-care-plus-heading border-[#e7e4db]'
											}`}
										>
											<FiClock size={16} className={isSelected ? 'text-white' : 'text-care-plus-heading'} />
											{formatTime(slot.start)}
										</button>
									);
								})}
							</div>
						</div>
					)}

					{/* Step: confirm */}
					{!loading && doctor && step === 'confirm' && selectedSlot && (
						<div id="care-profile-booking-confirm" className="flex flex-col">
							<ConfirmDetailRows
								rows={[
									{
										label: translator.translate(language, 'my_account', providerLabelKey),
										value: doctor.name,
									},
									{
										label: translator.translate(language, 'my_account', 'CareProfile/Booking/Date'),
										value: formatLongDate(selectedSlot.start),
									},
									{
										label: translator.translate(language, 'my_account', 'CareProfile/Booking/Time'),
										value: formatTime(selectedSlot.start),
									},
								]}
							/>

							{error && <p className="text-red-500 text-xs mb-3 text-center">{translator.translate(language, 'my_account', 'CareProfile/Booking/Error')}</p>}

							<button
								id="care-profile-booking-change"
								type="button"
								onClick={() => setStep('date')}
								className="w-full mb-3 py-3.5 rounded-[13px] border border-solid border-care-plus-orange text-care-plus-orange text-[15px] font-medium bg-white cursor-pointer"
							>
								{translator.translate(language, 'my_account', 'CareProfile/Booking/ChangeDateTime')}
							</button>
							<button
								id="care-profile-booking-confirm-button"
								type="button"
								onClick={handleConfirm}
								disabled={booking}
								className="w-full py-3.5 rounded-[13px] border-none bg-care-plus-button-gradient hover:bg-care-plus-button-gradient-hover text-white text-[15px] font-medium transition-all duration-200 disabled:opacity-70 cursor-pointer"
							>
								{translator.translate(language, 'my_account', booking ? 'CareProfile/Booking/Booking' : 'CareProfile/Booking/Confirm')}
							</button>
						</div>
					)}

					{/* Step: done */}
					{step === 'done' && selectedSlot && (
						<div id="care-profile-booking-done" className="flex flex-col">
							<div className="flex items-center gap-x-4 mb-8">
								<span className="flex items-center justify-center w-12 h-12 rounded-full border-2 border-[#00c950] flex-shrink-0">
									<FiCheck size={24} className="text-[#00c950]" />
								</span>
								<div className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-2 items-baseline min-w-0">
									<span className="text-sm text-[#616975] whitespace-nowrap">{translator.translate(language, 'my_account', 'CareProfile/Booking/Date')}</span>
									<span className="text-[15px] font-semibold text-care-plus-heading break-words">{formatLongDate(selectedSlot.start)}</span>
									<span className="text-sm text-[#616975] whitespace-nowrap">{translator.translate(language, 'my_account', 'CareProfile/Booking/Time')}</span>
									<span className="text-[15px] font-semibold text-care-plus-heading break-words">{formatTime(selectedSlot.start)}</span>
								</div>
							</div>

							<button
								id="care-profile-booking-done-button"
								type="button"
								onClick={onClose}
								className="w-full py-3.5 rounded-[13px] border-none bg-care-plus-button-gradient hover:bg-care-plus-button-gradient-hover text-white text-[15px] font-medium transition-all duration-200 cursor-pointer"
							>
								{translator.translate(language, 'my_account', 'CareProfile/Booking/Done')}
							</button>
							<button
								id="care-profile-booking-cancel"
								type="button"
								onClick={onClose}
								className="w-full mt-3 py-2 text-care-plus-orange text-[14px] font-medium underline bg-transparent border-none cursor-pointer"
							>
								{translator.translate(language, 'my_account', 'CareProfile/Booking/CancelAppointment')}
							</button>
						</div>
					)}
				</div>
			</div>
		</Sheet>
	);
};

export default BookConsultationSheet;
